import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import * as go from 'gojs';
import {
  type GojsDiagramColorTheme,
  type GojsDiagramLink,
  type GojsDiagramModel,
  type GojsDiagramNode,
} from '../../../model';
import { resolveGojsColorPalette, resolveGojsStructuralColors } from '../../../gojs/gojs-color.util';
import { createGojsLayout, hexToRgba, parseSimpleLatex } from '../../../gojs/gojs-diagram.util';
import { resolveGojsIcon } from '../../../gojs/gojs-icon.util';

@Injectable()
export class GojsDiagramEditorService {
  constructor(@Inject(DOCUMENT) private readonly document: Document) {}

  private diagram: go.Diagram | null = null;
  private palette: go.Palette | null = null;

  readonly selectedNodeData = signal<Record<string, unknown> | null>(null);
  readonly selectedLinkData = signal<Record<string, unknown> | null>(null);

  private selectionCallback: (() => void) | null = null;

  getDiagram(): go.Diagram | null {
    return this.diagram;
  }

  initDiagram(
    editorEl: HTMLDivElement,
    paletteEl: HTMLDivElement,
    layoutType: string,
    onSelectionChanged: () => void,
  ): void {
    this.selectionCallback = onSelectionChanged;
    const $ = go.GraphObject.make;

    this.diagram = $(go.Diagram, editorEl, {
      'undoManager.isEnabled': true,
      layout: createGojsLayout(layoutType),
      'linkingTool.isEnabled': true,
      'linkingTool.direction': go.LinkingDirection.ForwardsOnly,
      'relinkingTool.isEnabled': true,
      allowDrop: true,
      contentAlignment: go.Spot.Center,
      padding: new go.Margin(20),
      ChangedSelection: () => this.onSelectionChanged(),
      ModelChanged: (e) => {
        if (e.isTransactionFinished) {
          this.onSelectionChanged();
        }
      },
    });

    this.diagram.addDiagramListener('PartResized', (e) => {
      const resizedObj = e.subject as go.GraphObject;
      const node = resizedObj.part;
      if (node instanceof go.Node && this.diagram) {
        const size = resizedObj.desiredSize;
        this.diagram.model.startTransaction('sync resize');
        this.diagram.model.setDataProperty(node.data, 'node_width', Math.round(size.width));
        this.diagram.model.setDataProperty(node.data, 'node_height', Math.round(size.height));
        this.diagram.model.commitTransaction('sync resize');
        this.selectedNodeData.set({ ...(node.data as Record<string, unknown>) });
      }
    });

    this.setupNodeTemplates(this.diagram);
    this.setupLinkTemplate(this.diagram);
    this.setupPalette(paletteEl);
  }

  loadModel(model: GojsDiagramModel, showBackground: boolean): void {
    if (!this.diagram) {
      return;
    }

    const palette = resolveGojsColorPalette(this.document);
    const structural = resolveGojsStructuralColors(this.document);
    const neutral = palette['neutral'];
    const nodeDataArray = model.nodes.map((n, idx) => {
      const colors = showBackground ? palette[n.color_theme ?? 'neutral'] : neutral;
      let iconData: { icon_text: string; icon_src: string } = { icon_text: '', icon_src: '' };
      try {
        iconData = resolveGojsIcon(n.icon, structural.textDefault);
      } catch {
        // Icon resolution can fail for unknown FA icons
      }

      let bgColor = colors.bg;
      if (n.fill) {
        bgColor = n.fill_opacity !== undefined && n.fill_opacity < 1 ? hexToRgba(n.fill, n.fill_opacity) : n.fill;
      }

      let borderColor = colors.border;
      let hasBorder: boolean | number = n.border ?? false;
      if (n.stroke) {
        borderColor = n.stroke;
        hasBorder = n.stroke_width ?? 2;
      }

      let displayText = n.text ?? '';
      let textColor = n.text_color ?? colors.text;
      let isMath = false;
      if (n.latex) {
        const parsed = parseSimpleLatex(n.latex);
        displayText = parsed.text;
        if (parsed.color) {
          textColor = parsed.color;
        }
        isMath = true;
      }

      return {
        key: n.key,
        text: displayText,
        ...(isMath && { category: 'math' }),
        color_theme: n.color_theme ?? 'neutral',
        bg_color: bgColor,
        text_color: textColor,
        border_color: borderColor,
        has_border: hasBorder,
        shape: n.shape ?? 'RoundedRectangle',
        icon: n.icon ?? '',
        icon_text: iconData.icon_text,
        icon_src: iconData.icon_src,
        font_size: n.font_size ?? 14,
        font_weight: n.font_weight ?? 'normal',
        node_width: n.width,
        node_height: n.height,
        fill: n.fill,
        stroke: n.stroke,
        fill_opacity: n.fill_opacity,
        stroke_width: n.stroke_width,
        latex: n.latex,
        is_math: isMath,
        z_order: idx,
        ...(n.loc && { loc: n.loc }),
      };
    });

    const linkDataArray = model.links.map((l) => ({
      from: l.from,
      to: l.to,
      color: l.color ?? structural.linkColor,
      width: l.width ?? 1.5,
      to_arrow: l.to_arrow ?? 'Triangle',
      routing: l.routing ?? 'Orthogonal',
      curve: l.curve ?? 'None',
      curviness: l.curviness ?? NaN,
    }));

    this.diagram.model = new go.GraphLinksModel(nodeDataArray, linkDataArray);
  }

  extractModel(): { model: GojsDiagramModel; layoutType?: string; height?: number } | null {
    if (!this.diagram) {
      return null;
    }

    const structural = resolveGojsStructuralColors(this.document);
    const gm = this.diagram.model as go.GraphLinksModel;
    const nodes: GojsDiagramNode[] = gm.nodeDataArray.map((data: Record<string, unknown>) => {
      const node: GojsDiagramNode = { key: data['key'] as string };
      if (data['text'] && !data['latex']) {
        node.text = data['text'] as string;
      }
      if (data['icon']) {
        node.icon = data['icon'] as string;
      }
      const theme = data['color_theme'] as string | undefined;
      if (theme && theme !== 'neutral') {
        node.color_theme = theme as GojsDiagramColorTheme;
      }
      if (data['has_border']) {
        node.border = true;
      }
      if (data['shape'] && data['shape'] !== 'RoundedRectangle') {
        node.shape = data['shape'] as GojsDiagramNode['shape'];
      }
      if (data['font_size'] && data['font_size'] !== 14) {
        node.font_size = data['font_size'] as number;
      }
      if (data['font_weight'] && data['font_weight'] !== 'normal') {
        node.font_weight = data['font_weight'] as GojsDiagramNode['font_weight'];
      }
      if (data['node_width']) {
        node.width = data['node_width'] as number;
      }
      if (data['node_height']) {
        node.height = data['node_height'] as number;
      }
      if (data['fill']) {
        node.fill = data['fill'] as string;
      }
      if (data['stroke']) {
        node.stroke = data['stroke'] as string;
      }
      if (data['fill_opacity'] !== undefined && data['fill_opacity'] !== 1) {
        node.fill_opacity = data['fill_opacity'] as number;
      }
      if (data['stroke_width']) {
        node.stroke_width = data['stroke_width'] as number;
      }
      if (data['latex']) {
        node.latex = data['latex'] as string;
      }
      const goNode = this.diagram!.findNodeForKey(data['key'] as go.Key);
      if (goNode && goNode.location.isReal()) {
        node.loc = go.Point.stringify(goNode.location);
      }
      return node;
    });

    const links: GojsDiagramLink[] = gm.linkDataArray.map((data: Record<string, unknown>) => {
      const link: GojsDiagramLink = {
        from: data['from'] as string,
        to: data['to'] as string,
      };
      if (data['color'] && data['color'] !== structural.linkColor) {
        link.color = data['color'] as string;
      }
      if (data['width'] && data['width'] !== 1.5) {
        link.width = data['width'] as number;
      }
      if (data['to_arrow'] && data['to_arrow'] !== 'Triangle') {
        link.to_arrow = data['to_arrow'] as GojsDiagramLink['to_arrow'];
      }
      if (data['routing'] && data['routing'] !== 'Orthogonal') {
        link.routing = data['routing'] as GojsDiagramLink['routing'];
      }
      return link;
    });

    return { model: { nodes, links } };
  }

  applyLayout(layoutType: string, autoLayout: boolean): void {
    if (!this.diagram) {
      return;
    }
    this.diagram.layout = createGojsLayout(layoutType);
    if (autoLayout) {
      this.diagram.layoutDiagram(true);
    }
  }

  freezeLayout(): void {
    if (!this.diagram) {
      return;
    }
    this.diagram.nodes.each((node) => {
      if (node.location.isReal()) {
        this.diagram!.model.setDataProperty(node.data, 'loc', go.Point.stringify(node.location));
      }
    });
    this.diagram.layout.isOngoing = false;
    this.diagram.layout.isInitial = false;
  }

  applyNodeColors(showBackground: boolean): void {
    if (!this.diagram) {
      return;
    }
    const palette = resolveGojsColorPalette(this.document);
    const neutral = palette['neutral'];
    this.diagram.model.startTransaction('toggle colors');
    this.diagram.nodes.each((node) => {
      const data = node.data as Record<string, unknown>;
      if (showBackground) {
        const colors = palette[(data['color_theme'] as GojsDiagramColorTheme) ?? 'neutral'];
        this.diagram!.model.setDataProperty(data, 'bg_color', colors.bg);
        this.diagram!.model.setDataProperty(data, 'text_color', colors.text);
        this.diagram!.model.setDataProperty(data, 'border_color', colors.border);
      } else {
        this.diagram!.model.setDataProperty(data, 'bg_color', neutral.bg);
        this.diagram!.model.setDataProperty(data, 'text_color', neutral.text);
        this.diagram!.model.setDataProperty(data, 'border_color', neutral.border);
      }
    });
    this.diagram.model.commitTransaction('toggle colors');
  }

  setNodeDataProperty(prop: string, value: unknown): void {
    if (!this.diagram) {
      return;
    }
    const selected = this.diagram.selection.first();
    if (selected instanceof go.Node) {
      this.diagram.model.startTransaction('update node');
      this.diagram.model.setDataProperty(selected.data, prop, value);
      this.diagram.model.commitTransaction('update node');
      this.selectedNodeData.set({ ...(selected.data as Record<string, unknown>) });
    }
  }

  setLinkDataProperty(prop: string, value: unknown): void {
    if (!this.diagram) {
      return;
    }
    const selected = this.diagram.selection.first();
    if (selected instanceof go.Link) {
      this.diagram.model.startTransaction('update link');
      this.diagram.model.setDataProperty(selected.data, prop, value);
      this.diagram.model.commitTransaction('update link');
      this.selectedLinkData.set({ ...(selected.data as Record<string, unknown>) });
    }
  }

  destroy(): void {
    if (this.diagram) {
      this.diagram.div = null;
      this.diagram = null;
    }
    if (this.palette) {
      this.palette.div = null;
      this.palette = null;
    }
    this.selectionCallback = null;
  }

  private onSelectionChanged(): void {
    if (!this.diagram) {
      return;
    }

    const selected = this.diagram.selection.first();
    if (selected instanceof go.Node) {
      const data = selected.data as Record<string, unknown>;
      this.selectedNodeData.set({ ...data });
      this.selectedLinkData.set(null);
    } else if (selected instanceof go.Link) {
      this.selectedNodeData.set(null);
      const data = selected.data as Record<string, unknown>;
      this.selectedLinkData.set({ ...data });
    } else {
      this.selectedNodeData.set(null);
      this.selectedLinkData.set(null);
    }

    this.selectionCallback?.();
  }

  private setupNodeTemplates(diagram: go.Diagram): void {
    const $ = go.GraphObject.make;
    const structural = resolveGojsStructuralColors(this.document);

    // Default node template
    diagram.nodeTemplate = $(
      go.Node,
      'Auto',
      {
        locationSpot: go.Spot.Center,
        fromSpot: go.Spot.AllSides,
        toSpot: go.Spot.AllSides,
        fromLinkable: true,
        toLinkable: true,
        resizable: true,
        resizeObjectName: 'SHAPE',
        cursor: 'move',
      },
      new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
      new go.Binding('zOrder', 'z_order'),
      $(
        go.Shape,
        {
          name: 'SHAPE',
          figure: 'RoundedRectangle',
          strokeWidth: 0,
          stroke: 'transparent',
          fill: structural.defaultFill,
          parameter1: 8,
          portId: '',
          fromLinkable: true,
          toLinkable: true,
        },
        new go.Binding('figure', 'shape'),
        new go.Binding('fill', 'bg_color'),
        new go.Binding('stroke', 'border_color'),
        new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
          b ? (typeof b === 'number' ? b : 1.5) : 0,
        ),
        new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
          if (data['node_width'] && data['node_height']) {
            return new go.Size(data['node_width'] as number, data['node_height'] as number);
          }
          return new go.Size(NaN, NaN);
        }),
      ),
      $(
        go.Panel,
        'Horizontal',
        { margin: new go.Margin(8, 14) },
        new go.Binding('visible', '', (data: Record<string, unknown>) => {
          return !!(data['text'] || data['icon_text'] || data['icon_src']);
        }),
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            margin: new go.Margin(0, 4, 0, 0),
            visible: false,
          },
          new go.Binding('text', 'icon_text'),
          new go.Binding('visible', 'icon_text', (t) => !!t),
          new go.Binding('font', '', (data) => {
            const size = data.font_size ?? 14;
            return `500 ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
        $(
          go.Picture,
          {
            width: 18,
            height: 18,
            margin: new go.Margin(0, 6, 0, 0),
            visible: false,
            imageStretch: go.ImageStretch.Uniform,
          },
          new go.Binding('source', 'icon_src'),
          new go.Binding('visible', 'icon_src', (s) => !!s),
        ),
        $(
          go.TextBlock,
          {
            font: '14px Inter, system-ui, sans-serif',
            stroke: structural.textDefault,
            editable: true,
          },
          new go.Binding('text', 'text').makeTwoWay(),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data) => {
            const size = data.font_size ?? 14;
            if (data.is_math) {
              return `italic ${size}px "Times New Roman", Georgia, serif`;
            }
            const weight = data.font_weight === 'bold' ? 'bold' : '500';
            return `${weight} ${size}px Inter, system-ui, sans-serif`;
          }),
        ),
      ),
    );

    // Math/label node template
    diagram.nodeTemplateMap.add(
      'math',
      $(
        go.Node,
        'Spot',
        {
          locationSpot: go.Spot.Center,
          fromSpot: go.Spot.AllSides,
          toSpot: go.Spot.AllSides,
          fromLinkable: true,
          toLinkable: true,
          resizable: true,
          resizeObjectName: 'SHAPE',
          cursor: 'move',
          background: 'transparent',
        },
        new go.Binding('location', 'loc', go.Point.parse).makeTwoWay(go.Point.stringify),
        new go.Binding('zOrder', 'z_order'),
        $(
          go.Shape,
          {
            name: 'SHAPE',
            figure: 'RoundedRectangle',
            strokeWidth: 0,
            stroke: 'transparent',
            fill: structural.defaultFill,
            parameter1: 8,
            portId: '',
            fromLinkable: true,
            toLinkable: true,
          },
          new go.Binding('figure', 'shape'),
          new go.Binding('fill', 'bg_color'),
          new go.Binding('stroke', 'border_color'),
          new go.Binding('strokeWidth', 'has_border', (b: boolean | number) =>
            b ? (typeof b === 'number' ? b : 1.5) : 0,
          ),
          new go.Binding('desiredSize', '', (data: Record<string, unknown>) => {
            if (data['node_width'] && data['node_height']) {
              return new go.Size(data['node_width'] as number, data['node_height'] as number);
            }
            return new go.Size(NaN, NaN);
          }),
        ),
        $(
          go.TextBlock,
          {
            alignment: go.Spot.Center,
            font: 'italic 14px serif',
            stroke: structural.textDefault,
            editable: true,
          },
          new go.Binding('text', 'text').makeTwoWay(),
          new go.Binding('stroke', 'text_color'),
          new go.Binding('font', '', (data) => {
            const size = data.font_size ?? 14;
            return `italic ${size}px serif`;
          }),
        ),
      ),
    );
  }

  private setupLinkTemplate(diagram: go.Diagram): void {
    const $ = go.GraphObject.make;
    const structural = resolveGojsStructuralColors(this.document);

    diagram.linkTemplate = $(
      go.Link,
      {
        routing: go.Routing.Orthogonal,
        corner: 8,
        selectable: true,
        relinkableFrom: true,
        relinkableTo: true,
      },
      new go.Binding('routing', 'routing', (r: string) => {
        switch (r) {
          case 'Orthogonal':
            return go.Routing.Orthogonal;
          case 'AvoidsNodes':
            return go.Routing.AvoidsNodes;
          default:
            return go.Routing.Normal;
        }
      }),
      new go.Binding('curve', 'curve', (c: string) => (c === 'Bezier' ? go.Curve.Bezier : go.Curve.None)),
      new go.Binding('curviness', 'curviness'),
      $(
        go.Shape,
        { strokeWidth: 1.5, stroke: structural.linkColor },
        new go.Binding('stroke', 'color'),
        new go.Binding('strokeWidth', 'width'),
      ),
      $(
        go.Shape,
        { toArrow: 'Triangle', strokeWidth: 0, fill: structural.linkColor, scale: 0.8 },
        new go.Binding('fill', 'color'),
        new go.Binding('toArrow', 'to_arrow'),
        new go.Binding('visible', 'to_arrow', (a) => a !== 'None'),
      ),
    );
  }

  private setupPalette(paletteEl: HTMLDivElement): void {
    const $ = go.GraphObject.make;
    const structural = resolveGojsStructuralColors(this.document);

    this.palette = $(go.Palette, paletteEl, {
      nodeTemplate: $(
        go.Node,
        'Horizontal',
        { cursor: 'grab', margin: new go.Margin(4, 8) },
        $(
          go.Shape,
          {
            figure: 'RoundedRectangle',
            width: 28,
            height: 24,
            strokeWidth: 1,
            stroke: structural.paletteStroke,
            fill: structural.defaultFill,
            parameter1: 4,
          },
          new go.Binding('figure', 'shape'),
        ),
        $(
          go.TextBlock,
          {
            margin: new go.Margin(0, 0, 0, 8),
            font: '13px Inter, system-ui, sans-serif',
            stroke: structural.paletteText,
          },
          new go.Binding('text', 'text'),
        ),
      ),
      layout: $(go.GridLayout, {
        wrappingColumn: 1,
        cellSize: new go.Size(1, 1),
        spacing: new go.Size(0, 4),
      }),
    });

    this.palette.model = new go.GraphLinksModel([
      { text: 'Rectangle', shape: 'Rectangle', key: '__palette_rect' },
      { text: 'Rounded Rect', shape: 'RoundedRectangle', key: '__palette_rounded' },
      { text: 'Ellipse', shape: 'Ellipse', key: '__palette_ellipse' },
      { text: 'Diamond', shape: 'Diamond', key: '__palette_diamond' },
    ]);
  }
}
